import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';
import {
  getNextQuarterHour,
  parseHHMM,
  toHHMM
} from '../../utils/timePickerUtils';

const MINUTES = ['0', '15', '30', '45'];

interface TimePickerFieldProps {
  variableName: string;
  className?: string;
  ariaLabel?: string;
}

/**
 * Time picker: three spinners (hour 1-12, minute 0/15/30/45, AM/PM).
 * Outputs 24-hour "HH:MM" to the variable.
 */
const TimePickerField: React.FC<TimePickerFieldProps> = ({
  variableName,
  className,
  ariaLabel = 'Select time'
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const uiStore = useUIStore();

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialState = useMemo(() => {
    const parsed = parseHHMM(storedValue);
    if (parsed) {
      return {
        hour12: parsed.hour12,
        minute: parsed.minute,
        isPM: parsed.isPM
      };
    }
    const next = getNextQuarterHour();
    const hour12 = next.hour24 === 0 ? 12 : next.hour24 > 12 ? next.hour24 - 12 : next.hour24;
    const isPM = next.hour24 >= 12;
    return {
      hour12,
      minute: next.minute,
      isPM
    };
  }, []); // Only compute initial state once

  const [hour12, setHour12] = useState(initialState.hour12);
  const [minute, setMinute] = useState(initialState.minute);
  const [isPM, setIsPM] = useState(initialState.isPM);

  // Sync from stored value when it changes (e.g. broadcast from another client)
  useEffect(() => {
    const parsed = parseHHMM(storedValue);
    if (parsed) {
      setHour12(parsed.hour12);
      setMinute(parsed.minute);
      setIsPM(parsed.isPM);
      setError(null);
    }
  }, [storedValue]);

  const submitValue = useCallback(
    async (hhmm: string) => {
      if (hhmm === (storedValue ?? '').toString().trim()) return;

      try {
        setIsSaving(true);
        setError(null);
        await submitUserInput(variableName, hhmm);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update time';
        setError(message);
        uiStore.setError({
          code: 'TIME_PICKER_ERROR',
          message
        });
      } finally {
        setIsSaving(false);
      }
    },
    [variableName, storedValue, submitUserInput, uiStore]
  );

  const updateAndSubmit = useCallback(
    (newHour12: number, newMinute: number, newIsPM: boolean) => {
      const hhmm = toHHMM(newHour12, newMinute, newIsPM);
      void submitValue(hhmm);
    },
    [submitValue]
  );

  const cycleHourUp = useCallback(() => {
    const next = hour12 >= 12 ? 1 : hour12 + 1;
    setHour12(next);
    updateAndSubmit(next, minute, isPM);
  }, [hour12, minute, isPM, updateAndSubmit]);

  const cycleHourDown = useCallback(() => {
    const next = hour12 <= 1 ? 12 : hour12 - 1;
    setHour12(next);
    updateAndSubmit(next, minute, isPM);
  }, [hour12, minute, isPM, updateAndSubmit]);

  const cycleMinuteUp = useCallback(() => {
    const idx = MINUTES.indexOf(String(minute));
    const nextIdx = idx >= MINUTES.length - 1 ? 0 : idx + 1;
    const nextMin = Number(MINUTES[nextIdx] ?? 0);
    setMinute(nextMin);
    updateAndSubmit(hour12, nextMin, isPM);
  }, [hour12, minute, isPM, updateAndSubmit]);

  const cycleMinuteDown = useCallback(() => {
    const idx = MINUTES.indexOf(String(minute));
    const nextIdx = idx <= 0 ? MINUTES.length - 1 : idx - 1;
    const nextMin = Number(MINUTES[nextIdx] ?? 0);
    setMinute(nextMin);
    updateAndSubmit(hour12, nextMin, isPM);
  }, [hour12, minute, isPM, updateAndSubmit]);

  const cyclePeriod = useCallback(() => {
    const newIsPM = !isPM;
    setIsPM(newIsPM);
    updateAndSubmit(hour12, minute, newIsPM);
  }, [hour12, minute, isPM, updateAndSubmit]);

  // Initial submit: if we started with a default (next quarter) and store was empty, persist it
  const hasInitialized = React.useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const parsed = parseHHMM(storedValue);
    if (!parsed && (storedValue ?? '').toString().trim() === '') {
      const hhmm = toHHMM(initialState.hour12, initialState.minute, initialState.isPM);
      void submitValue(hhmm);
    }
  }, [storedValue, initialState, submitValue]);

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-time-picker'];
    if (className) classes.push(className);
    if (error) classes.push('rich-text-time-picker--error');
    return classes.join(' ');
  }, [className, error]);

  return (
    <div
      className={rootClassName}
      data-variable={variableName}
      data-testid={`time-picker-${variableName}`}
      role="group"
      aria-label={ariaLabel}
    >
      <div className="rich-text-time-picker__row">
        {/* Hour spinner */}
        <div className="rich-text-time-picker__spinner">
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
            onClick={cycleHourUp}
            disabled={isSaving}
            aria-label="Previous hour"
          >
            ▲
          </button>
          <span className="rich-text-spinner__value">{hour12}</span>
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
            onClick={cycleHourDown}
            disabled={isSaving}
            aria-label="Next hour"
          >
            ▼
          </button>
        </div>

        <span className="rich-text-time-picker__separator">:</span>

        {/* Minute spinner */}
        <div className="rich-text-time-picker__spinner">
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
            onClick={cycleMinuteUp}
            disabled={isSaving}
            aria-label="Previous minute"
          >
            ▲
          </button>
          <span className="rich-text-spinner__value">
            {String(minute).padStart(2, '0')}
          </span>
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
            onClick={cycleMinuteDown}
            disabled={isSaving}
            aria-label="Next minute"
          >
            ▼
          </button>
        </div>

        {/* AM/PM spinner */}
        <div className="rich-text-time-picker__spinner">
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--up"
            onClick={cyclePeriod}
            disabled={isSaving}
            aria-label="Toggle AM/PM"
          >
            ▲
          </button>
          <span className="rich-text-spinner__value">{isPM ? 'PM' : 'AM'}</span>
          <button
            type="button"
            className="rich-text-spinner__arrow rich-text-spinner__arrow--down"
            onClick={cyclePeriod}
            disabled={isSaving}
            aria-label="Toggle AM/PM"
          >
            ▼
          </button>
        </div>
      </div>

      {error && (
        <span
          className="rich-text-time-picker__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`time-picker-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default TimePickerField;
